import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './client'

// ── Types ──────────────────────────────────────────────────────────────

export interface PilotLead {
  id: number
  name: string
  business: string
  phone: string
  email: string
  use_case: string
  source: string
  ip: string
  user_agent: string
  created_at: number
  notified_at: number | null
  notify_error: string | null
}

interface PilotLeadsResponse {
  pilot_leads: PilotLead[]
  total: number
  limit: number
  offset: number
}

export interface PilotLeadCreatePayload {
  name: string
  business?: string
  phone?: string
  email: string
  use_case?: string
}

interface CreatePilotLeadResponse {
  ok: boolean
  id: number
}

// ── Public submit ──────────────────────────────────────────────────────

/**
 * POST a new pilot-lead application to the public endpoint. The route is
 * mounted with no auth (added to ``_PUBLIC_EXACT`` in the backend), so this
 * is callable from the unauthenticated marketing home page. On the server
 * side the row is persisted before the email-alert send is scheduled, so
 * a failed alert never blocks the form submission.
 */
export function createPilotLead(
  payload: PilotLeadCreatePayload,
): Promise<CreatePilotLeadResponse> {
  return apiFetch<CreatePilotLeadResponse>('/pilot-leads', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

// ── Superadmin list ────────────────────────────────────────────────────

/**
 * Superadmin-only paginated list. Lives at ``/pilot-leads/admin`` (not the
 * public ``/pilot-leads`` path) so the WorkOS middleware actually runs and
 * gates the request — see ``src/api/pilot_leads.py`` module docstring.
 */
export function usePilotLeads(params?: {
  search?: string
  limit?: number
  offset?: number
}) {
  const searchParams = new URLSearchParams()
  if (params?.search) searchParams.set('search', params.search)
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.offset) searchParams.set('offset', String(params.offset))
  const qs = searchParams.toString()
  return useQuery({
    queryKey: ['pilot-leads', params],
    queryFn: () =>
      apiFetch<PilotLeadsResponse>(
        `/pilot-leads/admin${qs ? `?${qs}` : ''}`,
      ),
    refetchInterval: 60_000,
  })
}
